import React from 'react'
import { Link } from '@contrast-security-inc/design-system-typography'
import { SessionMetadataField } from '.'
import { I18nConsumer } from '../i18n-provider'
import { OrderDropdown } from '../../legacy-design-system/order'
import { WithTooltip } from '../../design-system'
import { css } from '@emotion/css'
import { CsHelpCircledIcon as HelpIcon } from '../../legacy-design-system/icons'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { FormattedMessage } from 'react-intl'

export interface SessionMetadataFieldDropdownProps {
  readonly fields: SessionMetadataField[]
  readonly selectedFieldID: string
  readonly onChange: (item: string) => void
  readonly filterToGroup?: (filter: SessionMetadataField) => string
}

interface State {
  readonly selectedField: SessionMetadataField
}

export class SessionMetadataFieldDropdown extends React.Component<
  SessionMetadataFieldDropdownProps,
  State
> {
  constructor(props: SessionMetadataFieldDropdownProps) {
    super(props)
    const { selectedFieldID, fields, onChange } = props

    let selectedField = this.findFieldByID(fields, selectedFieldID)

    // sync global state with current state
    if (!selectedField) {
      selectedField = fields[0]
      onChange(selectedField.id)
    }
    this.state = {
      selectedField
    }
  }

  public componentWillReceiveProps({
    fields: newFields,
    selectedFieldID: newSelectedFieldID
  }: SessionMetadataFieldDropdownProps) {
    const { id: currentID, count: currentCount } = this.state.selectedField

    if (!!newSelectedFieldID) {
      const newField = newFields.find(({ id }) => newSelectedFieldID === id)
      if (
        !!newField &&
        (currentID !== newSelectedFieldID || newField.count !== currentCount)
      ) {
        this.setState({
          selectedField: newField
        })
      }
    }
  }

  public render() {
    const { selectedField } = this.state
    const { fields, onChange, filterToGroup } = this.props

    return (
      <React.Fragment>
        <WithTooltip
          renderTooltipContent={() => (
            <FormattedMessage id="detail_application_sessionMetadataDescription">
              {(message) => (
                <FormattedMessage id="action_learnMore">
                  {(linkText) => (
                    <HelpText>
                      {message}
                      <I18nConsumer>
                        {({ intl }) => (
                          <Link
                            className={css`
                              margin-left: 4px;
                              color: ${theme.color.turquoise50} !important;
                            `}
                            href={intl.formatMessage({
                              id: 'link_sessionMetadata'
                            })}
                            isExternal
                          >
                            {linkText}
                          </Link>
                        )}
                      </I18nConsumer>
                    </HelpText>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          )}
          placement="bottom"
        >
          {({ onMouseEnter, onMouseLeave }) => (
            <div
              className={css`
                margin-right: 5px;
              `}
            >
              <HelpIcon
                color={theme.color.neutral50}
                height={16}
                width={16}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            </div>
          )}
        </WithTooltip>

        <OrderDropdown<SessionMetadataField>
          items={fields}
          selectedItem={selectedField}
          selectionItemRenderer={({ item: { name } }) => (
            <FormattedMessage
              id="detail_application_viewBySessionMetadataField"
              values={{ fieldName: name }}
            />
          )}
          itemToString={({ name }) => name}
          itemToKey={({ id }) => id}
          itemToGroup={filterToGroup}
          onChange={({ id }) => onChange(id)}
          roundedCorners="all"
        />
      </React.Fragment>
    )
  }

  private findFieldByID = (fields: SessionMetadataField[], fieldID: string) =>
    fields.find(({ id }) => fieldID === id)
}

function HelpText({ children }) {
  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <span
        className={css`
          margin: 0;
          max-width: 330px;
          overflow-wrap: normal;
          white-space: normal;
        `}
      >
        {children}
      </span>
    </div>
  )
}
